import { downloadCsv, formatNumEsAr } from '@/components/features/shared/utils'
import { ApiResponse } from '@/domain'
import { InvoiceDetail } from '@/domain/invoices/invoice-item'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { formatInvoiceStatus, formatInvoiceType, UpdateInvoiceItemDescriptionDto, VoucherType } from '../../domain/invoices/invoice'
import { invoicesApi, type InvoiceFilters } from '../../rest/modules/invoices-api'
import { useToast } from '../use-toast'
import { isNullOrEmpty } from '@/utils/validations'

export const invoiceKeys = {
  all: ['invoices'] as const,
  dataLists: () => [...invoiceKeys.all, 'data-list'] as const,
}

export const useDataInvoices = (params?: InvoiceFilters) => {
  return useQuery({
    queryKey: [...invoiceKeys.dataLists(), params],
    queryFn: () => invoicesApi.getDataInvoices(params),
    staleTime: 100,
  })
}

export const useAgreementInvoices = (paymentIds: string[], options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['agreement-invoices', paymentIds],
    queryFn: () => invoicesApi.getAgreementInvoicesByPaymentIds(paymentIds),
    enabled: (options?.enabled ?? true) && !!paymentIds && paymentIds.length > 0,
    staleTime: 100,
  })
}

export const useGenerateInvoiceDocument = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, voucherType }: { id: string; voucherType: VoucherType }) => invoicesApi.generateInvoiceDocument(id, voucherType),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.dataLists() })

      toast({
        title: "Generación iniciada",
        description: "Se está procesando el documento. Este proceso puede tardar unos minutos.",
        variant: "success",
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Error al iniciar la generación",
        variant: "destructive",
      })
    },
  })
}

export const useDataInvoice = (id: string) => {
  return useQuery({
    queryKey: ['invoices', 'data-list', { id }],
    queryFn: () => invoicesApi.getDataInvoice(id),
    enabled: !!id,
    staleTime: 0,
  })
}

export const useUpdateInvoiceItems = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: Array<UpdateInvoiceItemDescriptionDto> }) =>
      invoicesApi.updateInvoiceItems(id, items),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.dataLists() })

      toast({
        title: "Items actualizados",
        description: "Las descripciones de los items fueron actualizadas correctamente.",
        variant: "success",
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Error al actualizar los items",
        variant: "destructive",
      })
    },
  })
}

const buildInvoicesCsv = (invoices: InvoiceDetail[]) => {
  const headers = [
    'Nro Comprobante',
    'Tipo Comprobante',
    'Documento Relacionado',
    'Tipo Documento Relacionado',
    'Contrato',
    'Codigo Cliente',
    'Razon Social',
    'Tipo Documento',
    'Numero Documento',
    'Fecha',
    'Total',
    'Estado',
  ]

  const rows = invoices.map((invoice) => {
    const date = invoice.updatedAt ? format(new Date(invoice.updatedAt), 'dd/MM/yyyy', { locale: es }) : '-'
    return [
      !isNullOrEmpty(invoice.invoiceNumber) ? `${invoice.pointOfSaleNumber}-${invoice.invoiceNumber}` : "-",
      formatInvoiceType(invoice.voucherType),
      invoice.relatedDocumentNumber ? String(invoice.relatedDocumentNumber) : '-',
      invoice.relatedDocumentType ? formatInvoiceType(invoice.relatedDocumentType) : '-',
      invoice.contractNumber,
      invoice.customerCode,
      `"${(invoice.customerName || '').replace(/"/g, '""')}"`,
      invoice.customerDocType,
      invoice.customerDocNumber,
      date,
      formatNumEsAr(invoice.totalAmount),
      formatInvoiceStatus(invoice.status, invoice.voucherType),
    ].join(';')
  })

  return '\uFEFF' + [headers.join(';'), ...rows].join('\n')
}

export const useExportInvoices = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: (params: InvoiceFilters) =>
      invoicesApi.getDataInvoices({ ...params, limit: -1 }),
    onSuccess: (response) => {
      const invoices = response?.data || []
      if (invoices.length === 0) return
      const csvContent = buildInvoicesCsv(invoices)
      const today = format(new Date(), 'yyyy-MM-dd')
      downloadCsv(csvContent, `facturacion_${today}.csv`)
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Error al obtener las facturas para exportar",
        variant: "destructive",
      })
    },
  })
}
